import {createApp} from "petite-vue";
import Swiper from "swiper";
import {Autoplay, EffectFade, Navigation, Pagination} from "swiper/modules";
import {NavigationOptions, PaginationOptions} from "swiper/types";
import {isInViewport} from "./utils/is-in-viewport";
import {initMap} from "./map";
import SimpleLightbox from "simple-lightbox";

import.meta.glob([ '@images/**' ])
import.meta.env.DEV && import("@styles/main.sass");

declare global {
    interface Window {
        mapLoaded: boolean;
    }

    const heroData: {
        heading: string
        subheading: string
        action: {
            url: string
            label: string
        }
    }[]

    interface pointDefinitions {
        title: string
        category: string
        distance: string
        image: string
        position: {
            lat: number
            lng: number
        }
        time: string
    }

    const pointsDefinitions: pointDefinitions[]
}

interface Header {
    opened: boolean
    handleOpenedHeader(state: boolean): void
}

const headerEl = document.querySelector('[data-header]'),
    headerScope: Header = {
        opened: false,
        handleOpenedHeader(state) {
            document.body.classList.toggle("u-hiddenOverflow", state)
        }
    }

headerEl && createApp(headerScope).mount(headerEl);

(() => {
    const homeHero = document.querySelector<HTMLElement>('[data-hero-swiper]');
    homeHero && new Swiper(homeHero, {
        slidesPerView: 1,
        modules: [Autoplay, Pagination, EffectFade],
        autoplay: {
            delay: 8000,
            //pauseOnMouseEnter: true
        },
        pagination: {
            el: "[data-hero-navigation]",
            type: "bullets",
            clickable: true,
            bulletActiveClass: "is-active",
            renderBullet: function (index, className) {
                return `<button class="${className}" aria-label="${heroData[index].heading.replace('&nbsp;', ' ')}"><span>${heroData[index].heading}</span></button>`
            }
        },
        speed: 800,
        effect: "fade",
        fadeEffect: {
            crossFade: true
        },
        on: {
            autoplayTimeLeft(s, time, progress) {
                homeHero.parentElement.style.setProperty("--heroSwiperProgress", (1 - progress).toFixed(6).toString());
            }
        }
    })

    const getNavigationOptions = (swiper: HTMLElement): NavigationOptions => {
        return {
            nextEl: swiper.querySelector<HTMLElement>('[data-navigation-button="next"]'),
            prevEl: swiper.querySelector<HTMLElement>('[data-navigation-button="prev"]')
        }
    }

    const getPaginationOptions = (swiper: HTMLElement): PaginationOptions => {
        return {
            type: "fraction",
            el: swiper.querySelector<HTMLElement>('[data-gallery-counts]'),
            formatFractionCurrent: function (number) {
                return number.toString().padStart(2, '0')
            },
            formatFractionTotal: function (number) {
                return number.toString().padStart(2, '0')
            },
            renderFraction: function (currentClass, totalClass) {
                return `<span class="${currentClass}"></span><span class="${totalClass}"></span>`
            }
        }
    }

    document.querySelectorAll<HTMLElement>('[data-design-swiper]').forEach(carousel => {
        new Swiper(carousel, {
            slidesPerView: 'auto',
            centeredSlides: true,
            spaceBetween: 12,
            loop: true,
            modules: [Navigation],
            navigation: getNavigationOptions(carousel),
            breakpoints: {
                640: {
                    spaceBetween: 20
                }
            }
        });
    })

    document.querySelectorAll<HTMLElement>('[data-gallery-carousel]').forEach(carousel => {
        const modules = [Navigation],
            hasNavigation = !!carousel.querySelector('[data-gallery-counts]')

        hasNavigation && modules.push(Pagination)

        new Swiper(carousel.querySelector<HTMLElement>('.swiper'), {
            slidesPerView: 'auto',
            spaceBetween: 12,
            modules: modules,
            //oneWayMovement: !hasNavigation,
            navigation: getNavigationOptions(carousel),
            pagination: getPaginationOptions(carousel),
            breakpoints: {
                640: {
                    spaceBetween: 20
                }
            }
        });
    })

    document.querySelectorAll<HTMLElement>('[data-design-carousel]').forEach(carousel => {
        new Swiper(carousel, {
            slidesPerView: 1,
            spaceBetween: 12,
            watchSlidesProgress: true,
            modules: [Navigation, Pagination],
            navigation: getNavigationOptions(carousel.parentElement),
            pagination: getPaginationOptions(carousel.parentElement),
            breakpoints: {
                512: {
                    slidesPerView: 2,
                    spaceBetween: 20
                },
                768: {
                    slidesPerView: 3
                }
            }
        });
    })
})();

(() => { // Count up numbers
    if (!document.querySelectorAll('[data-countable-number]')) return

    const animationDuration = 2400;
    const frameDuration = 1000 / 60;
    const totalFrames = Math.round( animationDuration / frameDuration );
    const easeOutQuad = t => t * ( 2 - t );

    const animateCountUp = (el: HTMLElement) => {
        if (el.classList.contains("is-counted"))
            return;

        el.classList.add("is-counted")
        el.innerHTML = el.innerHTML.replace(/([0-9]+)/g, '<span>$1</span>');
        let frame = 0;

        const number = el.querySelector('span')
        if (number.nextElementSibling?.nodeName === "SPAN") {
            number.innerHTML += number.nextElementSibling.innerHTML
            number.nextElementSibling.remove()
        }

        const countTo = parseInt( number.innerHTML, 10 );
        const counter = setInterval( () => {
            frame++;
            const progress = easeOutQuad( frame / totalFrames ),
                currentCount = Math.round( countTo * progress );

            if ( parseInt( number.innerHTML, 10 ) !== currentCount )
                number.innerHTML = currentCount.toLocaleString()

            frame === totalFrames && clearInterval( counter )
        }, frameDuration );
    };

    const runCountUp = () => {
        document.querySelectorAll('[data-countable-number]').forEach( (el: HTMLElement) => {
            isInViewport(el, 20) && animateCountUp(el);
        });
    }

    runCountUp();
    window.addEventListener("scroll", runCountUp);
})();

(() => { // Home offer selection
    interface OfferSelection {
        floor: number
        popoverData: {
            floor: string
            totalCount: number
            availableCount: number
        } | undefined
        setFloor(floor: number, scrollToRows: boolean): void
        showPopover(floor: number): void
        hidePopover(): void
    }

    const selectionWrapper = document.querySelector('[data-home-selection]') as HTMLElement,
        popoverElement = selectionWrapper?.querySelector('[popover]') as HTMLElement | null;

    if (!selectionWrapper || !popoverElement) {
        return;
    }
    const selection: OfferSelection = {
            floor: 2,
            popoverData: undefined,
            setFloor(floor, scrollToRows = false){
                this.floor = floor
                scrollToRows && document.querySelector('.HomeSelectionInner').scrollIntoView()
            },
            showPopover(floor) {
                const offers = offersData.filter(offer => offer.floor == floor)
                this.popoverData = {
                    floor: floor,
                    totalCount: offers.length,
                    availableCount: offers.filter(offer => offer.status.value === 'dostupny')?.length ?? 0
                }

                popoverElement.showPopover()
            },
            hidePopover() {
                popoverElement.hidePopover()
            }
        }

    const movePopover = (e: MouseEvent) => {
        let x: number, y: number

        try {
            x = e.pageX;
            y = e.pageY;
        } catch (e) {}

        popoverElement.style.setProperty('--popoverInset', `${y - 20}px auto auto ${x + 40}px`)
    };

    if (selectionWrapper) {
        createApp(selection).mount(selectionWrapper);
        selectionWrapper.addEventListener("mousemove", e => movePopover(e));
    }
})();

(() => {
    window.mapLoaded = false;

    const loadMap = () => {
        if (window.mapLoaded || !isInViewport(document.querySelector("[data-map-holder]"), 4))
            return;

        window.mapLoaded = true;
        initMap();
    };

    document.addEventListener("DOMContentLoaded", loadMap);
    document.addEventListener("scroll", loadMap);
})();

(() => { // Offer blueprint
    const blueprint = document.querySelector('[data-offer-blueprint]')
    if (!blueprint) return

    blueprint.querySelectorAll('svg > g').forEach(room => {
        room.addEventListener("click", () => {
            const target = document.querySelector(`[data-room-id="${room.id}"]`)?.closest('details')
            if (target) target.open = true
        })
    })

    const setDetailsHeight = (selector: string, wrapper = document) => {
        const setHeight = (detail: HTMLDetailsElement, open = false) => {
            detail.open = open;
            const rect = detail.getBoundingClientRect();
            detail.dataset.width = Math.floor(rect.width).toString();
            detail.style.setProperty(open ? `--expanded` : `--collapsed`,`${rect.height}px`);
        }

        const details = wrapper.querySelectorAll<HTMLDetailsElement>(selector);
        const RO = new ResizeObserver(entries => {
            return entries.forEach(entry => {
                const detail = entry.target as HTMLDetailsElement;
                const width = parseInt(detail.dataset.width);
                if (width !== Math.floor(entry.contentRect.width)) {
                    detail.removeAttribute('style');
                    setHeight(detail);
                    setHeight(detail, true);
                    detail.open = false;
                }
            })
        });

        details.forEach(detail => {
            RO.observe(detail);
        });

        setHeight(details[0], true)
    }

    /* Run it */
    setDetailsHeight('details');
})();

(() => {
    if (!document.querySelector(".wp-block-gallery")) return;
    new SimpleLightbox({ elements: ".wp-block-gallery figure > a" });
})();

(() => {
    if (!document.querySelector("[data-gallery]")) return;
    new SimpleLightbox({ elements: "[data-gallery] > a" });
})();

(() => {
    const toggle = document.querySelector('[data-offer-toggle]'),
        nav = document.querySelector('[data-floor-nav]')

    if (!toggle) return

    const getArchiveUrl = (url: string, state: boolean = false) => {
        const href = new URL(url),
            isEnglish = document.documentElement.lang.startsWith('en')

        const key = isEnglish ? 'possible-rent' : 'moznost-pronajmu'

        state ? href.searchParams.append(key, isEnglish ? 'yes' : 'ano') : href.searchParams.delete(key)

        return href.toString()
    }

    toggle.addEventListener("change", (e) => {
        const target = e.target as HTMLInputElement,
            state = target.value === 'pronajem'

        history.replaceState({}, '', getArchiveUrl(window.location.href, state))
        nav.querySelectorAll('a').forEach(link => {
            link.href = getArchiveUrl(link.href, state)
        })
    })
})();


(() => {
    const featurePoints = document.querySelectorAll('.FeaturePoint');

    featurePoints.forEach((featurePoint) => {
        const popoverId = featurePoint.getAttribute('popovertarget');
        const popoverElement = document.getElementById(popoverId);

        if (!popoverElement) return;

        function positionPopover() {
            const rect = featurePoint.getBoundingClientRect();
            const x = rect.left + window.scrollX + rect.width + 10;
            const y = rect.top + window.scrollY + rect.height / 2 - popoverElement.offsetHeight / 2 - 25;

            popoverElement.style.setProperty('--popoverInset', `${y}px auto auto ${x}px`);
        }

        featurePoint.addEventListener('mouseenter', () => {
            positionPopover();
            popoverElement.showPopover();
        });

        featurePoint.addEventListener('mouseleave', () => {
            popoverElement.hidePopover();
        });
    });
})();





